"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import "maplibre-gl/dist/maplibre-gl.css";
import "./map.css";
import {
  ChevronDown,
  Icon,
  IconButton,
  Layers,
  LocateFixed,
  Minus,
  Plus,
  RampStrip,
  RotateCcw,
  Swatch,
  cx,
} from "@/components";
import {
  filterExpression,
  getMapTokens,
  GLYPH_FONTSTACK_BOLD,
  legendModel,
  legendsMerge,
  SHAPE_BY_OTHER,
  shapeLegendModel,
  styleFromDescriptor,
  toGeoJSON,
  type MapSpec,
  type MarkerShape,
  type PointFeatureCollection,
  type PointSet,
  type TileDescriptor,
} from "@/lib/map";
import { circleRing, type Ring } from "@/lib/analysis/geometry";
import { metersBetween } from "@/lib/analysis/radius";
import {
  ANALYSIS_LAYERS,
  ANALYSIS_RADIUS_SOURCE,
  ANALYSIS_SOURCES,
  ANALYSIS_TERRITORY_LABEL_SOURCE,
  ANALYSIS_TERRITORY_SOURCE,
  ANALYSIS_DRIVETIME_SOURCE,
  ANALYSIS_ROUTE_SOURCE,
  ANALYSIS_ROUTE_STOP_SOURCE,
  ANALYSIS_DRAW_SOURCE,
  LAYER_DRAW_FILL,
  LAYER_DRAW_LINE,
  LAYER_DRIVETIME_FILL,
  LAYER_DRIVETIME_LINE,
  LAYER_RADIUS_FILL,
  LAYER_RADIUS_LINE,
  LAYER_ROUTE_LINE,
  LAYER_ROUTE_STOP,
  LAYER_ROUTE_STOP_LABEL,
  LAYER_TERRITORY_FILL,
  LAYER_TERRITORY_LABEL,
  LAYER_TERRITORY_LINE,
  drawFeatures,
  drawFillPaint,
  drawLinePaint,
  driveTimeFeatures,
  driveTimeFillPaint,
  driveTimeLinePaint,
  radiusFeatures,
  radiusFillPaint,
  radiusLinePaint,
  routeLineFeatures,
  routeLinePaint,
  routeStopFeatures,
  routeStopPaint,
  routeStopLabelPaint,
  territoryFeatures,
  territoryFillPaint,
  territoryLabelFeatures,
  territoryLabelPaint,
  territoryLinePaint,
} from "./analysisLayers";
import {
  ANNOTATIONS_SOURCE,
  LAYER_ANNOTATIONS,
  LAYER_CLUSTERS,
  LAYER_CLUSTER_COUNT,
  LAYER_HEAT,
  LAYER_HIGHLIGHT,
  LAYER_POINTS,
  LAYER_POINTS_PIN,
  LAYER_POINTS_PIN_DOT,
  LOW_CONFIDENCE_THRESHOLD,
  POINTS_SOURCE,
  annotationFeatures,
  annotationPaint,
  boundsForIds,
  clusterCategoryProperties,
  clusterCategorySignature,
  clusterPaint,
  dotLayerVisible,
  filteredCount,
  formatBreak,
  formatCellValue,
  heatPaint,
  highlightFilter,
  highlightPaint,
  isSymbolMarker,
  markerBodyLayout,
  markerDotLayout,
  markerSymbolPaint,
  matchesFilters,
  pinDotPaint,
  pointsFilter,
  pointsPaint,
} from "./mapLayers";
import { markerImages } from "./pinIcon";
import { SHAPE_ICONS } from "./shapeGlyph";
import {
  LAYER_OVERLAY_FILL,
  LAYER_OVERLAY_LINE,
  OVERLAY_BOUNDARY_SOURCE,
  overlayBoundaryFeatures,
  overlayFillPaint,
  overlayLinePaint,
  type ValueByBoundaryId,
} from "./overlayLayers";
import type { Boundary, Choropleth } from "@/lib/overlay/types";

export interface MapView {
  center: [number, number];
  zoom: number;
}

export interface MapCanvasProps {
  pointSet: PointSet;
  spec: MapSpec;
  /** Editor gets map chrome (zoom/reset/locate); embed gets none. Legend + attribution render in BOTH. */
  mode: "editor" | "embed";
  /** Vendor-clean tile descriptor, resolved server-side (`@/lib/map/server`). */
  tile: TileDescriptor;
  /** Optional Esri satellite descriptor (MAP-102) — enables the satellite basemap. */
  satelliteTile?: TileDescriptor;
  themeName: string;
  onViewChange?: (view: MapView) => void;
  /** M5 `highlight()` seam — row `_id`s to emphasize. */
  highlightIds?: string[];
  /** M5 annotation-badge seam — on-map labels (group-by aggregates, distance-to). */
  annotations?: Array<{ id: string; text: string }>;
  /**
   * M5 `fitTo()` seam — a QUERY `fit` effect. `ids` are the features to frame;
   * `nonce` re-triggers the fit even when the ids are unchanged. Honors
   * prefers-reduced-motion (snaps instead of flying).
   */
  fitRequest?: { ids: string[]; nonce: number } | null;
  /**
   * M6 seam — fires once the style has loaded, exposing the live MapLibre map
   * so surrounding chrome (viewer zoom column, PNG export capture, embed
   * pan-lock) can drive it without forking the renderer.
   */
  onReady?: (map: maplibregl.Map) => void;
  /**
   * M8 analysis draw mode (editor only). `"lasso"` freehand-draws a territory
   * polygon; `"radius"` drags a circle from a center. While set, map panning is
   * suspended so the gesture is a draw, not a pan. Embed never sets this — the
   * overlays still render from `spec.analysis`.
   */
  analysisDraw?: "lasso" | "radius" | null;
  /** Fires once a draw gesture completes with the resulting geometry. */
  onAnalysisDraw?: (
    result:
      | { kind: "lasso"; ring: [number, number][] }
      | { kind: "radius"; center: [number, number]; radiusMeters: number },
  ) => void;
  /**
   * M9 demographics/census choropleth data, resolved from the overlay engine
   * (boundaries + boundary→value map + classification) by the caller AFTER the
   * plan gate passes. Rides alongside `spec.overlay` (the persisted selection);
   * both editor and embed pass the same shape, so the choropleth redraws
   * identically. Null/absent → no choropleth (the empty layer stays inert).
   */
  overlayData?: OverlayRenderData | null;
  /**
   * M9 seam — fires with a census-boundary id when the choropleth fill is
   * clicked (the caller resolves the per-boundary user-data rollup). Only wired
   * when an overlay is present; a point/cluster click always takes precedence.
   */
  onBoundaryClick?: (boundaryId: string) => void;
  /** Cached map image shown when the browser cannot initialize WebGL. */
  fallbackImageUrl?: string | null;
  className?: string;
}

/** Resolved inputs the choropleth render layer needs (from `@/lib/overlay`). */
export interface OverlayRenderData {
  boundaries: Boundary[];
  values: ValueByBoundaryId;
  choropleth: Choropleth;
}

/* ------------------------------------------------------------------ */
/* Module singletons + environment helpers                             */
/* ------------------------------------------------------------------ */

let pmtilesRegistered = false;
function ensurePmtilesProtocol() {
  if (pmtilesRegistered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tile);
  pmtilesRegistered = true;
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Read a motion.* duration token off the document root (canvas can't use CSS vars directly). */
function motionMs(varName: string, fallback: number): number {
  if (prefersReducedMotion()) return 0;
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(varName);
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

const morphMs = () => motionMs("--motion-duration-map-morph", 600);
const flyMs = () => motionMs("--motion-duration-map-fly", 800);

/**
 * MapLibre requires absolute sprite/glyph URLs; same-origin descriptor paths
 * (the self-hosted default) resolve against the page origin at mount time.
 */
function absolutize(url: string): string {
  if (url === "" || !url.startsWith("/") || url.startsWith("//") || typeof window === "undefined") {
    return url;
  }
  return `${window.location.origin}${url}`;
}

function absolutizeTile(tile: TileDescriptor): TileDescriptor {
  return {
    ...tile,
    spec: absolutize(tile.spec),
    glyphs: absolutize(tile.glyphs),
    sprites: absolutize(tile.sprites),
  };
}

type MLMap = maplibregl.Map;
type StyleSpec = maplibregl.StyleSpecification;
type LayerSpec = Parameters<MLMap["addLayer"]>[0];
type FilterSpec = Parameters<MLMap["setFilter"]>[1];
type SourceSpec = Parameters<MLMap["addSource"]>[1];
type SourceDataSpec = Parameters<maplibregl.GeoJSONSource["setData"]>[0];

const DATA_LAYERS = [
  LAYER_HEAT,
  LAYER_CLUSTERS,
  LAYER_CLUSTER_COUNT,
  LAYER_POINTS,
  LAYER_POINTS_PIN,
  LAYER_POINTS_PIN_DOT,
  LAYER_HIGHLIGHT,
  LAYER_ANNOTATIONS,
];

/** The per-point marker layers (circle OR pin body) — the click/hover hit set. */
const MARKER_HIT_LAYERS = [LAYER_POINTS, LAYER_POINTS_PIN];

/** All SDF marker images (pin + FAIL-397 shapes) registered once per map; guarded against re-add. */
function ensureMarkerImages(map: maplibregl.Map) {
  for (const { id, image } of markerImages()) {
    if (!map.hasImage(id)) map.addImage(id, image, { sdf: true, pixelRatio: 2 });
  }
}

/** Identity key for the cluster aggregation setup (creation-only in MapLibre). */
function clusterSigKey(sig: { column: string; categories: string[] } | null): string {
  return sig ? `${sig.column}\u0000${sig.categories.join("\u0000")}` : "";
}

/** Identity key for which data lives in the source (full vs filtered subset). */
function dataKey(spec: MapSpec): string {
  const clustered = spec.clustering.enabled && !spec.heat.enabled;
  return clustered && spec.filters.length > 0 ? `filtered:${JSON.stringify(spec.filters)}` : "full";
}

interface HoverState {
  x: number;
  y: number;
  title: string;
  fields: Array<{ label: string; value: string }>;
}

/* ------------------------------------------------------------------ */
/* The renderer                                                        */
/* ------------------------------------------------------------------ */

/**
 * MapCanvas — the single MapLibre render path for editor, public URL, and
 * embed. One GeoJSON source + one declarative layer set toggled by MapSpec;
 * spec changes are DIFFED onto the live style (setPaintProperty / setFilter /
 * setClusterOptions / visibility) so a restyle never reloads, refetches, or
 * re-geocodes. Restyles interpolate over `motion.map.morph` (snap under
 * prefers-reduced-motion).
 */
export function MapCanvas({
  pointSet,
  spec,
  mode,
  tile,
  satelliteTile,
  themeName,
  onViewChange,
  highlightIds,
  annotations,
  fitRequest,
  onReady,
  analysisDraw,
  onAnalysisDraw,
  overlayData,
  onBoundaryClick,
  fallbackImageUrl,
  className,
}: MapCanvasProps) {
  const glRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const [renderFailure, setRenderFailure] = useState(false);
  const readyRef = useRef(false);
  const appliedSpecRef = useRef<MapSpec | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  // The map's ORIGINAL opening framing, captured at mount — what "Reset view"
  // returns to after the user pans/zooms away. Snapshotted so later edits to
  // `spec.view` (which tracks the live camera) can't drift it.
  const initialViewRef = useRef<{ fit: boolean; center: [number, number]; zoom: number } | null>(null);

  // Latest props for map callbacks (event handlers outlive renders).
  const specRef = useRef(spec);
  const pointSetRef = useRef(pointSet);
  const highlightRef = useRef<string[]>(highlightIds ?? []);
  const annotationsRef = useRef<Array<{ id: string; text: string }>>(annotations ?? []);
  const overlayDataRef = useRef<OverlayRenderData | null>(overlayData ?? null);
  const analysisDrawRef = useRef(analysisDraw ?? null);
  const onAnalysisDrawRef = useRef(onAnalysisDraw);
  useEffect(() => {
    onAnalysisDrawRef.current = onAnalysisDraw;
  }, [onAnalysisDraw]);
  const onBoundaryClickRef = useRef(onBoundaryClick);
  useEffect(() => {
    onBoundaryClickRef.current = onBoundaryClick;
  }, [onBoundaryClick]);
  /** Transient draw-gesture state (never a React render — the map owns it). */
  const drawRef = useRef<{ active: boolean; center: [number, number] | null; ring: Ring }>({
    active: false,
    center: null,
    ring: [],
  });
  const onViewChangeRef = useRef(onViewChange);
  useEffect(() => {
    onViewChangeRef.current = onViewChange;
  }, [onViewChange]);
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const tokens = useMemo(() => getMapTokens(themeName), [themeName]);
  const tokensRef = useRef(tokens);
  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  const geojson = useMemo(() => toGeoJSON(pointSet), [pointSet]);
  const geojsonRef = useRef(geojson);

  /** Same-origin asset paths made absolute (MapLibre requires absolute URLs). */
  const resolvedTileRef = useRef(tile);
  /** Satellite descriptor (MAP-102) — remote URLs, no absolutize needed. */
  const satelliteTileRef = useRef(satelliteTile);
  /**
   * Set true immediately before a programmatic camera re-assert (the MAP-102
   * satellite tile-load nudge) so the `moveend` it fires is swallowed instead of
   * being recorded as a user view change. Reset by the handler that consumes it.
   */
  const suppressViewChangeRef = useRef(false);

  const [hover, setHover] = useState<HoverState | null>(null);
  const [legendCollapsed, setLegendCollapsed] = useState(spec.legend.collapsed);

  /* ---------------------------------------------- pure-over-refs helpers */

  function sourceData(s: MapSpec): PointFeatureCollection {
    const full = geojsonRef.current;
    if (dataKey(s) === "full") return full;
    return {
      type: "FeatureCollection",
      features: full.features.filter((f) =>
        matchesFilters(f.properties as unknown as Record<string, string>, s.filters),
      ),
    };
  }

  function buildStyle(s: MapSpec): StyleSpec {
    return styleFromDescriptor(resolvedTileRef.current, tokensRef.current, {
      basemap: s.basemap,
      satellite: satelliteTileRef.current ?? null,
    }) as unknown as StyleSpec;
  }

  /* --------------------------------------------- M9 census choropleth */

  /** The choropleth boundary features for the current overlay data (empty when none). */
  function overlayFC(): ReturnType<typeof overlayBoundaryFeatures> {
    const od = overlayDataRef.current;
    if (!od) return { type: "FeatureCollection", features: [] };
    return overlayBoundaryFeatures(od.boundaries, od.values);
  }

  /**
   * Install the choropleth source + fill/line at the very BOTTOM of the stack (a
   * base thematic layer beneath every analysis overlay + marker). Always added —
   * inert (opacity 0, empty data) until `overlayData` + `spec.overlay` arrive.
   */
  function installOverlay(map: MLMap, s: MapSpec) {
    const t = tokensRef.current;
    const od = overlayDataRef.current;
    const opacity = s.overlay?.opacity ?? 0;
    map.addSource(OVERLAY_BOUNDARY_SOURCE, {
      type: "geojson",
      data: overlayFC() as unknown as SourceDataSpec,
    } as SourceSpec);
    map.addLayer({
      id: LAYER_OVERLAY_FILL,
      type: "fill",
      source: OVERLAY_BOUNDARY_SOURCE,
      paint: overlayFillPaint(od?.choropleth ?? null, t, opacity),
    } as unknown as LayerSpec);
    map.addLayer({
      id: LAYER_OVERLAY_LINE,
      type: "line",
      source: OVERLAY_BOUNDARY_SOURCE,
      paint: overlayLinePaint(t),
    } as unknown as LayerSpec);
  }

  /** Re-seat choropleth data + fill paint in place (no layer churn, no refetch). */
  function updateOverlay(map: MLMap, s: MapSpec) {
    const t = tokensRef.current;
    const od = overlayDataRef.current;
    const src = map.getSource(OVERLAY_BOUNDARY_SOURCE) as maplibregl.GeoJSONSource | undefined;
    src?.setData(overlayFC() as unknown as SourceDataSpec);
    if (map.getLayer(LAYER_OVERLAY_FILL)) {
      const paint = overlayFillPaint(od?.choropleth ?? null, t, s.overlay?.opacity ?? 0);
      for (const [name, value] of Object.entries(paint)) {
        map.setPaintProperty(LAYER_OVERLAY_FILL, name, value);
      }
    }
  }

  /* ------------------------------------------------ M8 analysis overlays */

  /** Add the analysis sources + the fill/line overlays that sit BENEATH markers. */
  function installAnalysisUnder(map: MLMap, s: MapSpec) {
    const t = tokensRef.current;
    const ps = pointSetRef.current;
    map.addSource(ANALYSIS_TERRITORY_SOURCE, {
      type: "geojson",
      data: territoryFeatures(ps, s.analysis) as unknown as SourceDataSpec,
    } as SourceSpec);
    map.addSource(ANALYSIS_TERRITORY_LABEL_SOURCE, {
      type: "geojson",
      data: territoryLabelFeatures(ps, s.analysis) as unknown as SourceDataSpec,
    } as SourceSpec);
    map.addSource(ANALYSIS_RADIUS_SOURCE, {
      type: "geojson",
      data: radiusFeatures(ps, s.analysis) as unknown as SourceDataSpec,
    } as SourceSpec);
    map.addSource(ANALYSIS_DRIVETIME_SOURCE, {
      type: "geojson",
      data: driveTimeFeatures(s.analysis) as unknown as SourceDataSpec,
    } as SourceSpec);
    map.addSource(ANALYSIS_ROUTE_SOURCE, {
      type: "geojson",
      data: routeLineFeatures(ps, s.analysis) as unknown as SourceDataSpec,
    } as SourceSpec);
    map.addSource(ANALYSIS_ROUTE_STOP_SOURCE, {
      type: "geojson",
      data: routeStopFeatures(ps, s.analysis) as unknown as SourceDataSpec,
    } as SourceSpec);
    map.addSource(ANALYSIS_DRAW_SOURCE, {
      type: "geojson",
      data: drawFeatures(null) as unknown as SourceDataSpec,
    } as SourceSpec);

    // Drive-time fills sit at the very bottom of the analysis stack.
    map.addLayer({
      id: LAYER_DRIVETIME_FILL,
      type: "fill",
      source: ANALYSIS_DRIVETIME_SOURCE,
      paint: driveTimeFillPaint(t),
    } as unknown as LayerSpec);
    map.addLayer({
      id: LAYER_DRIVETIME_LINE,
      type: "line",
      source: ANALYSIS_DRIVETIME_SOURCE,
      paint: driveTimeLinePaint(t),
    } as unknown as LayerSpec);
    map.addLayer({
      id: LAYER_TERRITORY_FILL,
      type: "fill",
      source: ANALYSIS_TERRITORY_SOURCE,
      paint: territoryFillPaint(),
    } as unknown as LayerSpec);
    map.addLayer({
      id: LAYER_TERRITORY_LINE,
      type: "line",
      source: ANALYSIS_TERRITORY_SOURCE,
      paint: territoryLinePaint(),
    } as unknown as LayerSpec);
    map.addLayer({
      id: LAYER_RADIUS_FILL,
      type: "fill",
      source: ANALYSIS_RADIUS_SOURCE,
      paint: radiusFillPaint(t),
    } as unknown as LayerSpec);
    map.addLayer({
      id: LAYER_RADIUS_LINE,
      type: "line",
      source: ANALYSIS_RADIUS_SOURCE,
      paint: radiusLinePaint(t),
    } as unknown as LayerSpec);
  }

  /** Add the label + route + transient draw overlays that sit ABOVE markers. */
  function installAnalysisOver(map: MLMap) {
    const t = tokensRef.current;
    // Route polyline + numbered stops render above markers (the tool's focus).
    map.addLayer({
      id: LAYER_ROUTE_LINE,
      type: "line",
      source: ANALYSIS_ROUTE_SOURCE,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: routeLinePaint(t),
    } as unknown as LayerSpec);
    map.addLayer({
      id: LAYER_ROUTE_STOP,
      type: "circle",
      source: ANALYSIS_ROUTE_STOP_SOURCE,
      paint: routeStopPaint(t),
    } as unknown as LayerSpec);
    if (resolvedTileRef.current.glyphs) {
      map.addLayer({
        id: LAYER_ROUTE_STOP_LABEL,
        type: "symbol",
        source: ANALYSIS_ROUTE_STOP_SOURCE,
        layout: {
          "text-field": ["get", "text"],
          "text-font": [GLYPH_FONTSTACK_BOLD],
          "text-size": 12,
          "text-allow-overlap": true,
        },
        paint: routeStopLabelPaint(t),
      } as unknown as LayerSpec);
    }
    map.addLayer({
      id: LAYER_DRAW_FILL,
      type: "fill",
      source: ANALYSIS_DRAW_SOURCE,
      paint: drawFillPaint(t),
    } as unknown as LayerSpec);
    map.addLayer({
      id: LAYER_DRAW_LINE,
      type: "line",
      source: ANALYSIS_DRAW_SOURCE,
      paint: drawLinePaint(t),
    } as unknown as LayerSpec);
    if (resolvedTileRef.current.glyphs) {
      map.addLayer({
        id: LAYER_TERRITORY_LABEL,
        type: "symbol",
        source: ANALYSIS_TERRITORY_LABEL_SOURCE,
        layout: {
          "text-field": ["get", "text"],
          "text-font": [GLYPH_FONTSTACK_BOLD],
          "text-size": 12,
          "text-allow-overlap": false,
          "text-optional": true,
        },
        paint: territoryLabelPaint(t),
      } as unknown as LayerSpec);
    }
  }

  /** Re-seat analysis source DATA in place (no layer churn, no refetch, no reload). */
  function updateAnalysisData(map: MLMap, s: MapSpec) {
    const ps = pointSetRef.current;
    const terr = map.getSource(ANALYSIS_TERRITORY_SOURCE) as maplibregl.GeoJSONSource | undefined;
    const label = map.getSource(ANALYSIS_TERRITORY_LABEL_SOURCE) as maplibregl.GeoJSONSource | undefined;
    const rad = map.getSource(ANALYSIS_RADIUS_SOURCE) as maplibregl.GeoJSONSource | undefined;
    const drive = map.getSource(ANALYSIS_DRIVETIME_SOURCE) as maplibregl.GeoJSONSource | undefined;
    const route = map.getSource(ANALYSIS_ROUTE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    const routeStops = map.getSource(ANALYSIS_ROUTE_STOP_SOURCE) as maplibregl.GeoJSONSource | undefined;
    terr?.setData(territoryFeatures(ps, s.analysis) as unknown as SourceDataSpec);
    label?.setData(territoryLabelFeatures(ps, s.analysis) as unknown as SourceDataSpec);
    rad?.setData(radiusFeatures(ps, s.analysis) as unknown as SourceDataSpec);
    drive?.setData(driveTimeFeatures(s.analysis) as unknown as SourceDataSpec);
    route?.setData(routeLineFeatures(ps, s.analysis) as unknown as SourceDataSpec);
    routeStops?.setData(routeStopFeatures(ps, s.analysis) as unknown as SourceDataSpec);
  }

  /** Install the source + the one declarative layer set for the given spec. */
  function install(map: MLMap, s: MapSpec) {
    const t = tokensRef.current;
    const ps = pointSetRef.current;
    const clustered = s.clustering.enabled && !s.heat.enabled;
    const sig = clusterCategorySignature(ps, s, t);
    const markersVisible = s.heat.enabled ? "none" : "visible";

    // The census choropleth is a base thematic layer — install at the very bottom.
    installOverlay(map, s);

    // Analysis fills/outlines/circles render BENEATH the markers — install next.
    installAnalysisUnder(map, s);

    const source: Record<string, unknown> = {
      type: "geojson",
      data: sourceData(s),
      cluster: clustered,
      clusterRadius: s.clustering.radius,
      clusterMaxZoom: s.clustering.maxZoom,
    };
    if (sig && sig.categories.length > 0) {
      source.clusterProperties = clusterCategoryProperties(sig.column, sig.categories);
    }
    map.addSource(POINTS_SOURCE, source as SourceSpec);

    const morph = { duration: morphMs() };
    const userFilter = filterExpression(s.filters);

    const heatLayer: Record<string, unknown> = {
      id: LAYER_HEAT,
      type: "heatmap",
      source: POINTS_SOURCE,
      layout: { visibility: s.heat.enabled ? "visible" : "none" },
      paint: heatPaint(s, t),
    };
    if (userFilter) heatLayer.filter = userFilter;
    map.addLayer(heatLayer as LayerSpec);

    map.addLayer({
      id: LAYER_CLUSTERS,
      type: "circle",
      source: POINTS_SOURCE,
      filter: ["has", "point_count"],
      layout: { visibility: markersVisible },
      paint: { ...clusterPaint(ps, s, t), "circle-color-transition": morph },
    } as unknown as LayerSpec);

    if (resolvedTileRef.current.glyphs) {
      map.addLayer({
        id: LAYER_CLUSTER_COUNT,
        type: "symbol",
        source: POINTS_SOURCE,
        filter: ["has", "point_count"],
        layout: {
          visibility: markersVisible,
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": [GLYPH_FONTSTACK_BOLD],
          // type.scale.micro (11px) — the §11 cluster-count register.
          "text-size": 11,
          "text-allow-overlap": true,
        },
        paint: { "text-color": t.cluster.text },
      } as unknown as LayerSpec);
    }

    // The circle layer + the one SDF symbol body layer + the pin's head-dot are
    // ALL installed; only the active shape is visible, so a marker switch is a
    // visibility toggle (+ an icon-image/anchor swap), never a layer rebuild — it
    // can't flash. The symbol layer renders whichever SDF the shape maps to (pin
    // or a FAIL-397 shape) with the same color/size encoding as the circle.
    const symbol = isSymbolMarker(s);
    ensureMarkerImages(map);

    map.addLayer({
      id: LAYER_POINTS,
      type: "circle",
      source: POINTS_SOURCE,
      filter: pointsFilter(s),
      layout: { visibility: symbol ? "none" : markersVisible },
      paint: {
        ...pointsPaint(ps, s, t),
        "circle-color-transition": morph,
        "circle-radius-transition": morph,
        "circle-stroke-color-transition": morph,
      },
    } as unknown as LayerSpec);

    map.addLayer({
      id: LAYER_POINTS_PIN,
      type: "symbol",
      source: POINTS_SOURCE,
      filter: pointsFilter(s),
      layout: { visibility: symbol ? markersVisible : "none", ...markerBodyLayout(ps, s) },
      paint: { ...markerSymbolPaint(ps, s, t), "icon-color-transition": morph },
    } as unknown as LayerSpec);

    map.addLayer({
      id: LAYER_POINTS_PIN_DOT,
      type: "symbol",
      source: POINTS_SOURCE,
      filter: pointsFilter(s),
      layout: { visibility: dotLayerVisible(s) ? markersVisible : "none", ...markerDotLayout(ps, s) },
      paint: pinDotPaint(ps, s, t),
    } as unknown as LayerSpec);

    map.addLayer({
      id: LAYER_HIGHLIGHT,
      type: "circle",
      source: POINTS_SOURCE,
      filter: highlightFilter(s, highlightRef.current),
      layout: { visibility: markersVisible },
      paint: highlightPaint(ps, s, t),
    } as unknown as LayerSpec);

    // M5 QUERY annotation badges live on their own text source (independent of
    // the filtered/clustered point source). The layer needs glyphs, like the
    // cluster-count layer — without them (dev) the source stays but paints nothing.
    map.addSource(ANNOTATIONS_SOURCE, {
      type: "geojson",
      data: annotationFeatures(ps, annotationsRef.current),
    } as SourceSpec);
    if (resolvedTileRef.current.glyphs) {
      map.addLayer({
        id: LAYER_ANNOTATIONS,
        type: "symbol",
        source: ANNOTATIONS_SOURCE,
        layout: {
          "text-field": ["get", "text"],
          "text-font": [GLYPH_FONTSTACK_BOLD],
          "text-size": 11,
          "text-offset": [0, -1.4],
          "text-anchor": "bottom",
          "text-allow-overlap": false,
          "text-optional": true,
        },
        paint: annotationPaint(t),
      } as unknown as LayerSpec);
    }

    // Territory labels + the transient draw preview render ABOVE markers.
    installAnalysisOver(map);
  }

  function removeDataLayers(map: MLMap) {
    for (const id of DATA_LAYERS) if (map.getLayer(id)) map.removeLayer(id);
    for (const id of ANALYSIS_LAYERS) if (map.getLayer(id)) map.removeLayer(id);
    for (const id of [LAYER_OVERLAY_FILL, LAYER_OVERLAY_LINE]) if (map.getLayer(id)) map.removeLayer(id);
    if (map.getSource(POINTS_SOURCE)) map.removeSource(POINTS_SOURCE);
    if (map.getSource(ANNOTATIONS_SOURCE)) map.removeSource(ANNOTATIONS_SOURCE);
    for (const id of ANALYSIS_SOURCES) if (map.getSource(id)) map.removeSource(id);
    if (map.getSource(OVERLAY_BOUNDARY_SOURCE)) map.removeSource(OVERLAY_BOUNDARY_SOURCE);
  }

  /** Re-seat source + layers from the in-memory data (never a network fetch). */
  function reinstall(map: MLMap, s: MapSpec) {
    removeDataLayers(map);
    install(map, s);
  }

  function fitToBounds(map: MLMap, animate: boolean) {
    const b = pointSetRef.current.bounds;
    if (!b) return;
    const bounds = new maplibregl.LngLatBounds([b.minLng, b.minLat], [b.maxLng, b.maxLat]);
    const canAnimate = animate && !prefersReducedMotion();
    map.fitBounds(bounds, {
      padding: 64,
      maxZoom: 14,
      animate: canAnimate,
      duration: canAnimate ? flyMs() : 0,
    });
  }

  /** Reset view — return to the map's ORIGINAL opening framing (captured at
   *  mount): a fit-to-data map re-fits the point bounds; a pinned map eases back
   *  to its opening center/zoom. Lets an owner who panned/zoomed away restore the
   *  view the map loads with, regardless of live edits to `spec.view`. */
  function resetView(map: MLMap) {
    const iv = initialViewRef.current;
    if (!iv) return;
    if (iv.fit) {
      fitToBounds(map, true);
      return;
    }
    if (prefersReducedMotion()) map.jumpTo({ center: iv.center, zoom: iv.zoom });
    else map.easeTo({ center: iv.center, zoom: iv.zoom, duration: flyMs() });
  }

  /** M5 `fitTo(ids)` — frame exactly the matched features (the `fit` QUERY effect). */
  function fitToPointIds(map: MLMap, ids: string[]) {
    const b = boundsForIds(pointSetRef.current, ids);
    if (!b) return;
    const bounds = new maplibregl.LngLatBounds([b.minLng, b.minLat], [b.maxLng, b.maxLat]);
    const canAnimate = !prefersReducedMotion();
    map.fitBounds(bounds, {
      padding: 96,
      maxZoom: 14,
      animate: canAnimate,
      duration: canAnimate ? flyMs() : 0,
    });
  }

  function setMarkerVisibility(map: MLMap, s: MapSpec) {
    const heatOn = s.heat.enabled;
    const markers = heatOn ? "none" : "visible";
    const symbol = isSymbolMarker(s);
    const set = (id: string, vis: "visible" | "none") => {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
    };
    // Only the active marker shape shows; the others stay hidden even when heat is
    // off. The circle is native; the symbol layer draws the active SDF shape; the
    // head dot is `pin`-only.
    set(LAYER_POINTS, symbol ? "none" : markers);
    set(LAYER_POINTS_PIN, symbol ? markers : "none");
    set(LAYER_POINTS_PIN_DOT, dotLayerVisible(s) ? markers : "none");
    set(LAYER_CLUSTERS, markers);
    set(LAYER_CLUSTER_COUNT, markers);
    set(LAYER_HIGHLIGHT, markers);
    set(LAYER_HEAT, heatOn ? "visible" : "none");
  }

  /**
   * Rewrite the COMPLETE marker state (visibility + every builder's full key set)
   * for circle / symbol body / pin dot / selection halo. Called for any marker OR
   * encoding change so a switch — shape ↔ shape, shape-by on/off, or between
   * shape-by columns — never strands stale GL state: each builder emits a stable
   * key set across all branches, so every prior key is overwritten in place. No
   * layer churn, no reload; purely idempotent setters.
   */
  function applyMarkerLayers(map: MLMap, next: MapSpec) {
    const t = tokensRef.current;
    const ps = pointSetRef.current;
    setMarkerVisibility(map, next);
    if (map.getLayer(LAYER_POINTS)) {
      for (const [name, value] of Object.entries(pointsPaint(ps, next, t))) {
        map.setPaintProperty(LAYER_POINTS, name, value);
      }
    }
    if (map.getLayer(LAYER_POINTS_PIN)) {
      const layout = markerBodyLayout(ps, next);
      for (const k of ["icon-image", "icon-anchor", "icon-size"] as const) {
        map.setLayoutProperty(LAYER_POINTS_PIN, k, layout[k]);
      }
      for (const [name, value] of Object.entries(markerSymbolPaint(ps, next, t))) {
        map.setPaintProperty(LAYER_POINTS_PIN, name, value);
      }
    }
    if (map.getLayer(LAYER_POINTS_PIN_DOT)) {
      const layout = markerDotLayout(ps, next);
      for (const k of ["icon-image", "icon-anchor", "icon-size"] as const) {
        map.setLayoutProperty(LAYER_POINTS_PIN_DOT, k, layout[k]);
      }
      for (const [name, value] of Object.entries(pinDotPaint(ps, next, t))) {
        map.setPaintProperty(LAYER_POINTS_PIN_DOT, name, value);
      }
    }
    if (map.getLayer(LAYER_HIGHLIGHT)) {
      for (const [name, value] of Object.entries(highlightPaint(ps, next, t))) {
        map.setPaintProperty(LAYER_HIGHLIGHT, name, value);
      }
    }
  }

  /**
   * The no-reload restyle path: compare the applied spec against the next one
   * and translate each change into targeted style-setter calls. The source's
   * FeatureCollection object is never refetched; geocoding is never touched.
   */

  /**
   * MAP-102 satellite tile-load nudge. MapLibre does not request a raster
   * source's tiles until the camera transform is invalidated: `setStyle` adds
   * the satellite raster source but leaves it dormant (paints the vector base +
   * labels but no imagery), and `map.resize()` is a no-op when the container
   * size is unchanged, so it does not suffice. Re-asserting the current camera —
   * identical center and zoom, so there is no visible movement — marks the
   * transform dirty and forces the tile-coverage recompute that kicks off the
   * imagery fetch. The `moveend` this fires is suppressed so it is not recorded
   * as a user view change (which would flip `view.fit` off and mark the map
   * dirty). No-op-safe for the vector variants, whose tiles are already present.
   */
  function forceBasemapTileLoad(map: MLMap) {
    suppressViewChangeRef.current = true;
    map.setCenter(map.getCenter());
  }

  function applyDiff(map: MLMap, prev: MapSpec, next: MapSpec) {
    const t = tokensRef.current;
    const ps = pointSetRef.current;

    // Basemap swap — transformStyle carries our source + data layers across,
    // so MapLibre diffs the basemap paint in place (no data reinstall at all).
    if (prev.basemap !== next.basemap) {
      const carrySources = [POINTS_SOURCE, ANNOTATIONS_SOURCE, ...ANALYSIS_SOURCES, OVERLAY_BOUNDARY_SOURCE];
      const carryLayers = new Set<string>([
        ...DATA_LAYERS,
        ...ANALYSIS_LAYERS,
        LAYER_OVERLAY_FILL,
        LAYER_OVERLAY_LINE,
      ]);
      map.setStyle(buildStyle(next), {
        diff: true,
        transformStyle: (previous, nextStyle) => ({
          ...nextStyle,
          sources: {
            ...nextStyle.sources,
            ...Object.fromEntries(
              carrySources
                .filter((id) => previous?.sources?.[id])
                .map((id) => [id, previous!.sources[id]!]),
            ),
          },
          layers: [...nextStyle.layers, ...(previous?.layers ?? []).filter((l) => carryLayers.has(l.id))],
        }),
      });
      // MAP-102: kick the newly-added raster source into loading its tiles
      // (see forceBasemapTileLoad). Runs on every basemap swap — a no-op beyond
      // a transform re-assert for the vector variants.
      forceBasemapTileLoad(map);
    }

    // Marker shape/size change — all marker layers already exist, so this is a
    // visibility toggle plus setters (no rebuild, no flash). A shape change swaps
    // the symbol's icon-image + icon-anchor (pin↔shape) as well as its size, and
    // realigns the selection halo — applyMarkerLayers rewrites the full key set.
    if (JSON.stringify(prev.marker) !== JSON.stringify(next.marker)) {
      applyMarkerLayers(map, next);
    }

    const src = map.getSource(POINTS_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (!src) return;

    const prevClustered = prev.clustering.enabled && !prev.heat.enabled;
    const nextClustered = next.clustering.enabled && !next.heat.enabled;
    const sigChanged =
      clusterSigKey(clusterCategorySignature(ps, prev, t)) !==
      clusterSigKey(clusterCategorySignature(ps, next, t));

    // Cluster aggregation properties are creation-only: when the categorical
    // signature changes while clusters are (or become) live, re-seat the
    // source + layers from memory. Every other path below is a pure setter.
    if (sigChanged && (prevClustered || nextClustered)) {
      reinstall(map, next);
      return;
    }

    if (
      prevClustered !== nextClustered ||
      prev.clustering.radius !== next.clustering.radius ||
      prev.clustering.maxZoom !== next.clustering.maxZoom
    ) {
      src.setClusterOptions({
        cluster: nextClustered,
        clusterRadius: next.clustering.radius,
        clusterMaxZoom: next.clustering.maxZoom,
      });
    }

    if (dataKey(prev) !== dataKey(next)) {
      src.setData(sourceData(next) as unknown as SourceDataSpec);
    }

    const filtersChanged = JSON.stringify(prev.filters) !== JSON.stringify(next.filters);
    if (filtersChanged) {
      map.setFilter(LAYER_POINTS, pointsFilter(next) as FilterSpec);
      for (const id of [LAYER_POINTS_PIN, LAYER_POINTS_PIN_DOT]) {
        if (map.getLayer(id)) map.setFilter(id, pointsFilter(next) as FilterSpec);
      }
      if (map.getLayer(LAYER_HEAT)) {
        map.setFilter(LAYER_HEAT, (filterExpression(next.filters) ?? null) as FilterSpec);
      }
      map.setFilter(LAYER_HIGHLIGHT, highlightFilter(next, highlightRef.current) as FilterSpec);
    }

    const encodingChanged = JSON.stringify(prev.encoding) !== JSON.stringify(next.encoding);
    if (encodingChanged) {
      // color-by → fills/tints, size-by → radii/icon-size, shape-by (MAP-24) →
      // circle↔symbol visibility + per-value icon-image/anchor/size. All of it is
      // the full marker rewrite; cluster tint follows the same categorical signature.
      applyMarkerLayers(map, next);
      if (map.getLayer(LAYER_CLUSTERS)) {
        map.setPaintProperty(LAYER_CLUSTERS, "circle-color", clusterPaint(ps, next, t)["circle-color"]);
      }
    }

    const heatChanged = JSON.stringify(prev.heat) !== JSON.stringify(next.heat);
    if (heatChanged) {
      setMarkerVisibility(map, next);
      if (map.getLayer(LAYER_HEAT)) {
        for (const [name, value] of Object.entries(heatPaint(next, t))) {
          map.setPaintProperty(LAYER_HEAT, name, value);
        }
      }
    }
    // Analysis overlays (M8): a spec.analysis change re-seats the overlay
    // sources' data IN PLACE — no layer churn, no data refetch, no reload.
    if (JSON.stringify(prev.analysis) !== JSON.stringify(next.analysis)) {
      updateAnalysisData(map, next);
    }
    // Overlay (M9): a spec.overlay change (opacity / classification / ramp) re-seats
    // the choropleth fill paint + boundary data in place — no reload, no refetch.
    if (JSON.stringify(prev.overlay) !== JSON.stringify(next.overlay)) {
      updateOverlay(map, next);
    }

    // view/legend/popup changes are UI state — never a map mutation here
    // (the view is user-owned once the map is live; fit is explicit).
  }

  /* ----------------------------------------------------- interactions */

  function hitLayers(map: MLMap): string[] {
    return [...MARKER_HIT_LAYERS, LAYER_CLUSTERS].filter((id) => map.getLayer(id) !== undefined);
  }

  function openPopup(map: MLMap, feature: maplibregl.MapGeoJSONFeature) {
    const s = specRef.current;
    if (!s.popup.enabled) return;
    const ps = pointSetRef.current;
    const props = (feature.properties ?? {}) as Record<string, unknown>;
    const typeOf = (col: string) => ps.columns.find((c) => c.name === col)?.type;

    const node = document.createElement("div");
    const titleCol = s.popup.titleColumn;
    const title = titleCol ? String(props[titleCol] ?? "").trim() : "";
    if (title !== "") {
      const h = document.createElement("p");
      h.className = "pz-map-popup__title";
      h.textContent = title;
      node.appendChild(h);
    }
    const rows = document.createElement("div");
    rows.className = "pz-map-popup__rows";
    for (const field of s.popup.fields) {
      if (field === titleCol) continue;
      const key = document.createElement("span");
      key.className = "pz-map-popup__key";
      key.textContent = field;
      const val = document.createElement("p");
      val.className = "pz-map-popup__val";
      val.textContent = formatCellValue(String(props[field] ?? ""), typeOf(field));
      rows.append(key, val);
    }
    if (rows.childNodes.length > 0) node.appendChild(rows);
    const confidence = Number(props._confidence ?? 1);
    if (confidence < LOW_CONFIDENCE_THRESHOLD) {
      const warn = document.createElement("p");
      warn.className = "pz-map-popup__accuracy";
      warn.textContent = "Approximate location";
      node.appendChild(warn);
    }
    if (node.childNodes.length === 0) return;

    popupRef.current?.remove();
    const coords = (feature.geometry as { coordinates: [number, number] }).coordinates;
    popupRef.current = new maplibregl.Popup({
      className: "pz-map-popup",
      closeButton: true,
      maxWidth: "320px",
    })
      .setLngLat(coords)
      .setDOMContent(node)
      .addTo(map);
  }

  function expandCluster(map: MLMap, feature: maplibregl.MapGeoJSONFeature) {
    const src = map.getSource(POINTS_SOURCE) as maplibregl.GeoJSONSource | undefined;
    const clusterId = Number((feature.properties as Record<string, unknown>)?.cluster_id);
    if (!src || !Number.isFinite(clusterId)) return;
    const center = (feature.geometry as { coordinates: [number, number] }).coordinates;
    src
      .getClusterExpansionZoom(clusterId)
      .then((zoom) => {
        if (prefersReducedMotion()) map.jumpTo({ center, zoom });
        else map.easeTo({ center, zoom, duration: morphMs() });
      })
      .catch(() => {
        /* cluster dissolved mid-flight — nothing to expand */
      });
  }

  function handleHover(map: MLMap, e: maplibregl.MapMouseEvent) {
    if (!readyRef.current) return;
    if (drawRef.current.active || analysisDrawRef.current) {
      // Draw gestures own the cursor + suppress the point hovertip.
      if (hover) setHover(null);
      return;
    }
    const layers = hitLayers(map);
    const feature = layers.length > 0 ? map.queryRenderedFeatures(e.point, { layers })[0] : undefined;
    if (!feature) {
      map.getCanvas().style.cursor = "";
      setHover(null);
      return;
    }
    map.getCanvas().style.cursor = "pointer";
    // Hovertip only for individual markers (circle or pin body) — never clusters.
    if (!MARKER_HIT_LAYERS.includes(feature.layer.id)) {
      setHover(null);
      return;
    }
    const s = specRef.current;
    const ps = pointSetRef.current;
    const props = (feature.properties ?? {}) as Record<string, unknown>;
    const titleCol = s.popup.titleColumn ?? ps.columns[0]?.name;
    const title = titleCol ? String(props[titleCol] ?? "").trim() : "";
    const typeOf = (col: string) => ps.columns.find((c) => c.name === col)?.type;
    const fields = s.popup.fields
      .filter((field) => field !== titleCol)
      .map((field) => ({
        label: field,
        value: formatCellValue(String(props[field] ?? ""), typeOf(field)),
      }))
      .filter(({ value }) => value !== "—");
    setHover({
      x: e.point.x,
      y: e.point.y,
      title: title !== "" ? title : "Point",
      fields,
    });
  }

  /* ------------------------------------------------ M8 draw gestures */

  function setDrawData(map: MLMap, ring: Ring | null) {
    const src = map.getSource(ANALYSIS_DRAW_SOURCE) as maplibregl.GeoJSONSource | undefined;
    src?.setData(drawFeatures(ring) as unknown as SourceDataSpec);
  }

  function handleDrawDown(e: maplibregl.MapMouseEvent) {
    const mode = analysisDrawRef.current;
    if (!mode) return;
    const ll: [number, number] = [e.lngLat.lng, e.lngLat.lat];
    drawRef.current = {
      active: true,
      center: mode === "radius" ? ll : null,
      ring: mode === "lasso" ? [ll] : [],
    };
  }

  function handleDrawMove(map: MLMap, e: maplibregl.MapMouseEvent) {
    const st = drawRef.current;
    if (!st.active) return;
    const mode = analysisDrawRef.current;
    const ll: [number, number] = [e.lngLat.lng, e.lngLat.lat];
    if (mode === "lasso") {
      st.ring.push(ll);
      setDrawData(map, st.ring);
    } else if (mode === "radius" && st.center) {
      st.ring = circleRing(st.center, metersBetween(st.center, ll));
      setDrawData(map, st.ring);
    }
  }

  function handleDrawUp(map: MLMap, e: maplibregl.MapMouseEvent) {
    const st = drawRef.current;
    if (!st.active) return;
    const mode = analysisDrawRef.current;
    const ll: [number, number] = [e.lngLat.lng, e.lngLat.lat];
    drawRef.current = { active: false, center: null, ring: [] };
    setDrawData(map, null);
    if (mode === "lasso" && st.ring.length >= 3) {
      onAnalysisDrawRef.current?.({ kind: "lasso", ring: st.ring });
    } else if (mode === "radius" && st.center) {
      const radiusMeters = metersBetween(st.center, ll);
      if (radiusMeters > 0) onAnalysisDrawRef.current?.({ kind: "radius", center: st.center, radiusMeters });
    }
  }

  /* ------------------------------------------------------- lifecycle */

  // Mount once. The tile descriptor, theme, and mode are per-surface constants;
  // everything dynamic (spec, data, highlights) flows through the diff effects.
  useEffect(() => {
    const container = glRef.current;
    if (!container) return;
    ensurePmtilesProtocol();
    resolvedTileRef.current = absolutizeTile(tile);

    const s0 = specRef.current;
    // undefined `fit` opens at center/zoom (the `if (view.fit)` framing below
    // treats it falsy), so mirror that here.
    initialViewRef.current = { fit: s0.view.fit ?? false, center: s0.view.center, zoom: s0.view.zoom };
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container,
        style: buildStyle(s0),
        center: s0.view.center,
        zoom: s0.view.zoom,
        attributionControl: false,
        dragRotate: false,
        pitchWithRotate: false,
      });
    } catch {
      container.replaceChildren();
      window.setTimeout(() => setRenderFailure(true), 0);
      return;
    }
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;
    if (process.env.NODE_ENV !== "production") {
      // Dev-only handle so headless smoke tests can queryRenderedFeatures.
      (container as unknown as { __plotzioMap?: MLMap }).__plotzioMap = map;
    }

    // Missing dev assets (pmtiles archive / glyphs / sprites) surface as map
    // error events; keep them off the console — the paper ground still paints
    // (B4's placeholder rule) and markers don't depend on basemap assets.
    map.on("error", (e) => {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[map]", e.error?.message ?? e);
      }
    });

    map.on("load", () => {
      install(map, specRef.current);
      readyRef.current = true;
      appliedSpecRef.current = specRef.current;
      if (specRef.current.view.fit) fitToBounds(map, false);
      onReadyRef.current?.(map);
      // MAP-102: a map that MOUNTS on the satellite basemap (a saved satellite
      // map in the viewer/embed/detail) carries the raster source in its initial
      // style; nudge it so the imagery loads even if the initial render leaves
      // the raster dormant. Guarded to satellite so ordinary vector mounts don't
      // pay a needless transform re-assert.
      if (specRef.current.basemap === "satellite") forceBasemapTileLoad(map);
    });

    map.on("moveend", () => {
      // Swallow the moveend from a programmatic tile-load nudge (MAP-102) — it
      // is not a user view change and must not flip view.fit off / mark dirty.
      if (suppressViewChangeRef.current) {
        suppressViewChangeRef.current = false;
        return;
      }
      const c = map.getCenter();
      onViewChangeRef.current?.({ center: [c.lng, c.lat], zoom: map.getZoom() });
    });
    map.on("mousedown", (e) => handleDrawDown(e));
    map.on("mousemove", (e) => {
      handleDrawMove(map, e);
      handleHover(map, e);
    });
    map.on("mouseup", (e) => handleDrawUp(map, e));
    map.on("click", (e) => {
      if (!readyRef.current) return;
      // In a draw mode, clicks are part of the gesture — never popups.
      if (analysisDrawRef.current) return;
      const layers = hitLayers(map);
      const feature = layers.length > 0 ? map.queryRenderedFeatures(e.point, { layers })[0] : undefined;
      if (!feature) {
        // No marker/cluster hit — a census-boundary click resolves the rollup.
        if (onBoundaryClickRef.current && map.getLayer(LAYER_OVERLAY_FILL)) {
          const bf = map.queryRenderedFeatures(e.point, { layers: [LAYER_OVERLAY_FILL] })[0];
          const id = bf ? (bf.properties as Record<string, unknown> | null)?._id : undefined;
          if (id != null) onBoundaryClickRef.current(String(id));
        }
        return;
      }
      if (feature.layer.id === LAYER_CLUSTERS) expandCluster(map, feature);
      else openPopup(map, feature);
    });

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      readyRef.current = false;
      appliedSpecRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Spec changed → diff onto the live style. Never re-fetches, never re-fits.
  useEffect(() => {
    specRef.current = spec;
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    const prev = appliedSpecRef.current;
    if (!prev || prev === spec) return;
    applyDiff(map, prev, spec);
    appliedSpecRef.current = spec;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec]);

  // New dataset (re-Make map) → re-seat from memory and re-fit. Coordinates
  // arrive already resolved in the PointSet; nothing here geocodes.
  useEffect(() => {
    const previous = geojsonRef.current;
    geojsonRef.current = geojson;
    pointSetRef.current = pointSet;
    const map = mapRef.current;
    if (!map || !readyRef.current || previous === geojson) return;
    popupRef.current?.remove();
    reinstall(map, specRef.current);
    appliedSpecRef.current = specRef.current;
    if (specRef.current.view.fit) fitToBounds(map, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geojson, pointSet]);

  // Highlight overlay (M5 seam) — a filter swap, nothing else moves.
  useEffect(() => {
    highlightRef.current = highlightIds ?? [];
    const map = mapRef.current;
    if (!map || !readyRef.current || !map.getLayer(LAYER_HIGHLIGHT)) return;
    map.setFilter(
      LAYER_HIGHLIGHT,
      highlightFilter(specRef.current, highlightRef.current) as FilterSpec,
    );
  }, [highlightIds]);

  // Annotation badges (M5 seam) — a source-data swap on the dedicated text source.
  useEffect(() => {
    annotationsRef.current = annotations ?? [];
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    const src = map.getSource(ANNOTATIONS_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (src) {
      src.setData(annotationFeatures(pointSetRef.current, annotationsRef.current) as unknown as SourceDataSpec);
    }
  }, [annotations]);

  // Census choropleth data (M9 seam) — re-seat the boundary source + fill paint
  // in place. Editor and embed pass the same shape, so the redraw is identical.
  useEffect(() => {
    overlayDataRef.current = overlayData ?? null;
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    updateOverlay(map, specRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayData]);

  // Fit-to-matches (M5 `fit` QUERY effect) — nonce re-fires even for the same ids.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current || !fitRequest || fitRequest.ids.length === 0) return;
    fitToPointIds(map, fitRequest.ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitRequest?.nonce]);

  // M8 draw mode (editor) — suspend panning so the gesture draws, restore on exit.
  useEffect(() => {
    analysisDrawRef.current = analysisDraw ?? null;
    drawRef.current = { active: false, center: null, ring: [] };
    const map = mapRef.current;
    if (!map) return;
    if (readyRef.current) {
      const src = map.getSource(ANALYSIS_DRAW_SOURCE) as maplibregl.GeoJSONSource | undefined;
      src?.setData(drawFeatures(null) as unknown as SourceDataSpec);
    }
    if (analysisDraw) {
      map.dragPan.disable();
      map.doubleClickZoom.disable();
      map.getCanvas().style.cursor = "crosshair";
    } else {
      map.dragPan.enable();
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = "";
    }
  }, [analysisDraw]);

  /* ---------------------------------------------------------- overlays */

  const legend = useMemo(() => {
    const col = spec.encoding.colorBy?.column;
    const values = col ? pointSet.points.map((p) => p.props[col] ?? "") : [];
    return legendModel(spec.encoding.colorBy, values, tokens.palette, tokens.paletteOther, tokens.sequential);
  }, [pointSet, spec.encoding.colorBy, tokens]);

  // Shape-by legend (MAP-24) — value→shape rows, shown as glyphs beside the colors.
  const shapeLegend = useMemo(() => {
    const col = spec.encoding.iconBy?.column;
    const values = col ? pointSet.points.map((p) => p.props[col] ?? "") : [];
    return shapeLegendModel(spec.encoding.iconBy, values);
  }, [pointSet, spec.encoding.iconBy]);

  // Same column + categorical color → one merged block (glyphs tinted by color);
  // otherwise the color rows and shape rows render as two labelled sections.
  const legendsAreMerged = legendsMerge(spec.encoding.colorBy, spec.encoding.iconBy);

  /** Shape for a color entry's value (used only in the merged block). */
  const shapeForValue = useMemo(() => {
    const map = new Map<string, MarkerShape>();
    if (shapeLegend.kind === "shape") for (const e of shapeLegend.entries) map.set(e.value, e.shape);
    return map;
  }, [shapeLegend]);

  const showing = useMemo(() => filteredCount(pointSet, spec.filters), [pointSet, spec.filters]);

  const legendVisible =
    spec.legend.enabled &&
    (spec.heat.enabled || legend.kind !== "none" || shapeLegend.kind !== "none");

  const legendTitle = spec.heat.enabled
    ? "Density"
    : legend.kind !== "none"
      ? legend.column
      : shapeLegend.kind === "shape"
        ? shapeLegend.column
        : "";

  return (
    <div className={cx("pz-mapcanvas", spec.basemap === "dark" && "pz-mapcanvas--dark", className)}>
      <div ref={glRef} className="pz-mapcanvas__gl" />

      {renderFailure && (
        <div className="pz-mapcanvas__fallback" role="status">
          {fallbackImageUrl ? (
            // The cached image is already the server-rendered representation of this map.
            // eslint-disable-next-line @next/next/no-img-element
            <img src={fallbackImageUrl} alt="" className="pz-mapcanvas__fallback-image" />
          ) : null}
          <div className="pz-mapcanvas__fallback-message">
            <strong>Interactive map unavailable</strong>
            <span>This browser could not start map graphics. The map data is still available.</span>
          </div>
        </div>
      )}

      {mode === "editor" && !renderFailure && (
        <div className="pz-map-controls">
          <IconButton label="Zoom in" tooltip="left" onClick={() => mapRef.current?.zoomIn({ duration: morphMs() })}>
            <Icon icon={Plus} size="md" />
          </IconButton>
          <IconButton label="Zoom out" tooltip="left" onClick={() => mapRef.current?.zoomOut({ duration: morphMs() })}>
            <Icon icon={Minus} size="md" />
          </IconButton>
          <IconButton
            label="Reset view"
            tooltip="left"
            onClick={() => {
              const map = mapRef.current;
              if (map) resetView(map);
            }}
          >
            <Icon icon={RotateCcw} size="md" />
          </IconButton>
          <IconButton
            label="Go to my location"
            tooltip="left"
            onClick={() => {
              navigator.geolocation?.getCurrentPosition(
                (pos) => {
                  const map = mapRef.current;
                  if (!map) return;
                  const center: [number, number] = [pos.coords.longitude, pos.coords.latitude];
                  const zoom = Math.max(map.getZoom(), 11);
                  if (prefersReducedMotion()) map.jumpTo({ center, zoom });
                  else map.easeTo({ center, zoom, duration: flyMs() });
                },
                () => {
                  /* denied/unavailable — the map stays where it is */
                },
              );
            }}
          >
            <Icon icon={LocateFixed} size="md" />
          </IconButton>
        </div>
      )}

      {spec.filters.length > 0 && (
        <span className="pz-map-countchip">
          Showing {showing.toLocaleString()} of {pointSet.count.toLocaleString()} points
        </span>
      )}

      {legendVisible && (
        <div className="pz-map-legendwrap">
          {legendCollapsed ? (
            <IconButton label="Show legend" onClick={() => setLegendCollapsed(false)}>
              <Icon icon={Layers} size="md" />
            </IconButton>
          ) : (
            <div className="pz-legend">
              <span className="pz-map-legend__head">
                <span className="pz-legend__title">{legendTitle}</span>
                <button
                  type="button"
                  className="pz-map-legend__collapse"
                  aria-label="Collapse legend"
                  onClick={() => setLegendCollapsed(true)}
                >
                  <Icon icon={ChevronDown} size="sm" />
                </button>
              </span>
              {spec.heat.enabled ? (
                <RampStrip stops={tokens.heat.slice(1)} startLabel="sparse" endLabel="dense" />
              ) : legendsAreMerged && legend.kind === "categorical" ? (
                // Merged (MAP-24): one row per value — its shape glyph, tinted its color.
                legend.entries.map((entry) => (
                  <span key={entry.value} className="pz-legend__row">
                    <span className="pz-map-legend__glyph" style={{ color: entry.color }}>
                      <Icon
                        icon={SHAPE_ICONS[entry.isOther ? SHAPE_BY_OTHER : shapeForValue.get(entry.value) ?? SHAPE_BY_OTHER]}
                        size="sm"
                      />
                    </span>
                    <span
                      className={cx("pz-map-legend__label", entry.isOther && "pz-map-legend__label--other")}
                    >
                      {entry.value}
                    </span>
                  </span>
                ))
              ) : (
                <>
                  {legend.kind === "categorical"
                    ? legend.entries.map((entry) => (
                        <span key={entry.value} className="pz-legend__row">
                          <Swatch color={entry.color} />
                          <span
                            className={cx("pz-map-legend__label", entry.isOther && "pz-map-legend__label--other")}
                          >
                            {entry.value}
                          </span>
                        </span>
                      ))
                    : legend.kind === "numeric" ? (
                        <RampStrip
                          stops={legend.stops.map((stop) => stop.color)}
                          startLabel={formatBreak(legend.stops[0]?.value ?? 0)}
                          endLabel={formatBreak(legend.stops[legend.stops.length - 1]?.value ?? 0)}
                        />
                      ) : null}
                  {/* Shape-by section (MAP-24) — its own labelled block when it can't merge. */}
                  {shapeLegend.kind === "shape" && (
                    <>
                      {legend.kind !== "none" && (
                        <span className="pz-map-legend__subhead">{shapeLegend.column}</span>
                      )}
                      {shapeLegend.entries.map((entry) => (
                        <span key={entry.value} className="pz-legend__row">
                          <span className="pz-map-legend__glyph">
                            <Icon icon={SHAPE_ICONS[entry.shape]} size="sm" />
                          </span>
                          <span
                            className={cx("pz-map-legend__label", entry.isOther && "pz-map-legend__label--other")}
                          >
                            {entry.value}
                          </span>
                        </span>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Attribution — non-removable in every mode (rendered unconditionally). */}
      <span className="pz-map-attribution">{tile.attribution}</span>

      {hover && (
        <span className="pz-map-hovertip" style={{ left: hover.x, top: hover.y }}>
          <span className="pz-map-hovertip__title">{hover.title}</span>
          {hover.fields.map(({ label, value }) => (
            <span className="pz-map-hovertip__meta" key={label}>
              <span className="pz-map-hovertip__label">{label}</span>
              {value}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
